import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Play, Download, RefreshCw } from "lucide-react";

const DEFAULT_SCRIPT = `Welcome to BizMatch! I'm your AI guide, and I'm excited to show you how our platform makes selling your business simple and effective.

Here's how BizMatch works in four easy steps:

Step 1: List Your Business. Create a detailed listing with photos, financials, and business details. Brokers pay just $89 per month per listing, and private sellers pay $98 per month - with no upfront fees, making it risk-free to get started.

Step 2: We Find Buyers. Our advanced AI technology matches your business with qualified buyers from our database of over 4,000 pre-screened purchasers. No more waiting around hoping someone will find your listing.

Step 3: Connect and Close. You'll receive qualified leads directly through the platform, connecting you with serious buyers who are genuinely interested in your business.

Step 4: Track Your Progress. Monitor your listing performance, lead quality, and conversion rates through your comprehensive analytics dashboard. Stay informed every step of the way.

With BizMatch, you get transparency, efficiency, and results. Ready to get started? Click the button below to list your first business today!`;

export function VideoManager() {
  const [script, setScript] = useState(DEFAULT_SCRIPT);
  const [isGenerating, setIsGenerating] = useState(false);
  const [pollingStatus, setPollingStatus] = useState<string | null>(null);
  const [currentVideoUrl, setCurrentVideoUrl] = useState<string | null>(null);
  const [videoFileName, setVideoFileName] = useState<string | null>(null);
  const [manualVideoId, setManualVideoId] = useState("");
  const [isFinalizing, setIsFinalizing] = useState(false);
  const { toast } = useToast();
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    checkExistingVideo();
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, []);

  const checkExistingVideo = async () => {
    try {
      const { data } = await supabase.storage
        .from('ai-avatar-videos')
        .list('', { limit: 1, sortBy: { column: 'created_at', order: 'desc' } });

      if (data?.length) {
        const { data: urlData } = supabase.storage.from('ai-avatar-videos').getPublicUrl(data[0].name);
        setCurrentVideoUrl(urlData.publicUrl);
        setVideoFileName(data[0].name);
      }
    } catch (e) {
      console.error('Error checking existing video:', e);
    }
  };

  const pollForCompletion = (videoId: string) => {
    setPollingStatus('Processing video...');
    let attempts = 0;
    const maxAttempts = 90; // ~3 minutes

    pollingRef.current = setInterval(async () => {
      attempts++;
      if (attempts > maxAttempts) {
        clearInterval(pollingRef.current!);
        pollingRef.current = null;
        setIsGenerating(false);
        setPollingStatus(null);
        toast({ title: "Timeout", description: "Video generation timed out. Try again later.", variant: "destructive" });
        return;
      }

      try {
        const { data, error } = await supabase.functions.invoke('check-video-status', {
          body: { video_id: videoId }
        });

        if (error) throw error;

        const status = data?.status;
        setPollingStatus(`Status: ${status} (${attempts}/${maxAttempts})`);

        if (status === 'done' && data?.result_url) {
          clearInterval(pollingRef.current!);
          pollingRef.current = null;
          setPollingStatus('Uploading to storage...');

          // Finalize: download from HeyGen and upload to Supabase storage
          const { data: finalData, error: finalError } = await supabase.functions.invoke('finalize-video', {
            body: { video_url: data.result_url }
          });

          if (finalError) throw finalError;

          setCurrentVideoUrl(finalData.video_url);
          setVideoFileName(finalData.file_name);
          setIsGenerating(false);
          setPollingStatus(null);
          toast({ title: "Success", description: "Video generated and uploaded!" });
        } else if (status === 'failed') {
          clearInterval(pollingRef.current!);
          pollingRef.current = null;
          setIsGenerating(false);
          setPollingStatus(null);
          toast({ title: "Failed", description: typeof data?.error === 'string' ? data.error : JSON.stringify(data?.error) || "Video generation failed.", variant: "destructive" });
        }
      } catch (e) {
        console.error('Polling error:', e);
      }
    }, 2000);
  };

  const generateVideo = async (forceRegenerate = false) => {
    setIsGenerating(true);

    try {
      if (!forceRegenerate) {
        await checkExistingVideo();
        if (currentVideoUrl) {
          toast({ title: "Video Already Exists", description: "Click 'Regenerate' to create a new one." });
          setIsGenerating(false);
          return;
        }
      }

      const { data, error } = await supabase.functions.invoke('admin-generate-video', {
        body: { text: script, force_regenerate: forceRegenerate }
      });

      if (error) throw error;

      if (data?.video_id) {
        // Async flow: poll for completion
        pollForCompletion(data.video_id);
      } else if (data?.video_url) {
        // Existing video returned
        setCurrentVideoUrl(data.video_url);
        setIsGenerating(false);
        toast({ title: "Video Found", description: data.message });
      } else {
        throw new Error('Unexpected response');
      }
    } catch (error: any) {
      console.error('Error generating video:', error);
      setIsGenerating(false);
      setPollingStatus(null);
      toast({ title: "Error", description: typeof error?.message === 'string' ? error.message : JSON.stringify(error?.message) || 'Failed to generate video', variant: "destructive" });
    }
  };

  return (
    <Card className="w-full max-w-4xl mx-auto">
      <CardHeader>
        <CardTitle>AI Avatar Video Manager</CardTitle>
        <CardDescription>Generate and manage the AI avatar presentation video</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <label className="text-sm font-medium">Video Script</label>
          <Textarea value={script} onChange={(e) => setScript(e.target.value)} className="min-h-[200px]" />
          <p className="text-xs text-muted-foreground">{script.length}/2000 characters</p>
        </div>

        {pollingStatus && (
          <div className="p-3 bg-muted rounded-lg flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span className="text-sm">{pollingStatus}</span>
          </div>
        )}

        {currentVideoUrl && !isGenerating && (
          <div className="p-4 bg-muted rounded-lg">
            <h4 className="font-medium mb-2">Current Video</h4>
            <p className="text-sm text-muted-foreground mb-3">{videoFileName}</p>
            <div className="flex gap-2">
              <Button onClick={() => window.open(currentVideoUrl!, '_blank')} variant="outline" size="sm">
                <Play className="w-4 h-4 mr-1" /> Preview
              </Button>
              <Button onClick={() => { const a = document.createElement('a'); a.href = currentVideoUrl!; a.download = videoFileName || 'video.mp4'; a.click(); }} variant="outline" size="sm">
                <Download className="w-4 h-4 mr-1" /> Download
              </Button>
            </div>
          </div>
        )}

        <div className="flex gap-3">
          <Button onClick={() => generateVideo(false)} disabled={isGenerating || !script.trim()}>
            {isGenerating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Play className="w-4 h-4 mr-1" />}
            {currentVideoUrl ? 'Check for Video' : 'Generate Video'}
          </Button>
          {currentVideoUrl && (
            <Button onClick={() => generateVideo(true)} disabled={isGenerating || !script.trim()} variant="outline">
              {isGenerating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
              Regenerate
            </Button>
          )}
        </div>

        <div className="p-4 border rounded-lg space-y-3">
          <h4 className="font-medium">Finalize Existing HeyGen Video</h4>
          <p className="text-sm text-muted-foreground">Paste a HeyGen video ID to download and upload it to storage.</p>
          <div className="flex gap-2">
            <input
              type="text"
              value={manualVideoId}
              onChange={(e) => setManualVideoId(e.target.value)}
              placeholder="e.g. df263f1cee204c0ea1c0152020574e2d"
              className="flex-1 px-3 py-2 border rounded-md text-sm"
            />
            <Button
              onClick={async () => {
                if (!manualVideoId.trim()) return;
                setIsFinalizing(true);
                try {
                  // Step 1: Get the download URL
                  const { data: statusData, error: statusError } = await supabase.functions.invoke('check-video-status', {
                    body: { video_id: manualVideoId.trim() }
                  });
                  if (statusError) throw statusError;
                  if (statusData?.status !== 'done' || !statusData?.result_url) {
                    throw new Error(`Video not ready. Status: ${statusData?.status}`);
                  }
                  // Step 2: Finalize (download + upload to storage)
                  const { data: finalData, error: finalError } = await supabase.functions.invoke('finalize-video', {
                    body: { video_url: statusData.result_url }
                  });
                  if (finalError) throw finalError;
                  setCurrentVideoUrl(finalData.video_url);
                  setVideoFileName(finalData.file_name);
                  setManualVideoId("");
                  toast({ title: "Success", description: "Video finalized and uploaded!" });
                } catch (e: any) {
                  toast({ title: "Error", description: e.message || "Failed to finalize video", variant: "destructive" });
                } finally {
                  setIsFinalizing(false);
                }
              }}
              disabled={isFinalizing || !manualVideoId.trim()}
              variant="outline"
              size="sm"
            >
              {isFinalizing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Download className="w-4 h-4 mr-1" />}
              Finalize
            </Button>
          </div>
        </div>

        <div className="p-4 bg-muted rounded-lg border-l-4 border-primary">
          <h4 className="font-medium mb-2">Instructions</h4>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li>• Video generation takes 1-3 minutes — progress shown above</li>
            <li>• Click "Regenerate" to create a new video with updated script</li>
            <li>• Or paste a HeyGen video ID to finalize an existing video</li>
            <li>• The video will be automatically displayed on the homepage</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}
